import type { ReactNode } from "react";
import { useMemo } from "react";
import type { PlanRoom, PlanSegment } from "@sts/geometry";

/**
 * The floor from above, with one room lit and one of its walls picked out.
 *
 * "Wall 1" and "Wall 2" are what the scan called them, and standing in a
 * bathroom they mean nothing: a surveyor was being asked what is behind a wall
 * they could not identify.
 *
 * THE WHOLE FLOOR, NOT THE ROOM ALONE. The first version drew only the room
 * being answered, and a 2.2 m² cupboard on its own is a grey slab at an angle
 * — it says nothing about WHERE that cupboard is, which is the thing somebody
 * standing in the flat needs. Every room on the storey is drawn, the one being
 * answered is filled and outlined, and the rest are faint. The question "which
 * wall" is only answerable against the rooms either side of it.
 *
 * DELIBERATELY NOT THE PLAN EDITOR. No zoom, no pan, no dimensions, no
 * objects, no toolbar. It is read in a second and nothing on it can be clicked
 * by mistake while somebody is standing on a ladder.
 *
 * NORTH IS NOT CLAIMED. A RoomPlan capture's axes are wherever the phone was
 * when the session began, so this is "the shape of the floor as it was
 * walked", not an oriented plan. Drawing a compass would be inventing a
 * bearing.
 */

/** Pixels per metre in the viewBox; the SVG is then fitted to its box. */
const SCALE = 60;

/** Room around the shape, so a wall on the edge is not clipped by its stroke. */
const PAD = 12;

export function RoomMiniPlan({
  rooms,
  activeRef,
  highlightId,
  highlightLabel,
}: {
  /** Every room on this storey, so the lit one has somewhere to be. */
  readonly rooms: readonly PlanRoom[];
  /** `PlanRoom.externalRef` of the room being answered. */
  readonly activeRef: string;
  /** The surface being answered: `PlanSegment.id`, the RoomPlan element. */
  readonly highlightId: string | null;
  readonly highlightLabel?: string | undefined;
}): ReactNode {
  const active = useMemo(
    () => rooms.find((r) => r.externalRef === activeRef) ?? null,
    [rooms, activeRef],
  );

  const box = useMemo(() => bounds(rooms), [rooms]);
  if (box === null || active === null) return null;

  const width = box.maxX - box.minX + PAD * 2;
  const height = box.maxY - box.minY + PAD * 2;
  /** Metres to viewBox units, origin at the floor's corner. */
  const shift = (p: { x: number; y: number }) => ({
    x: p.x * SCALE - box.minX + PAD,
    // Plan Y grows "up" in the capture and down in SVG, so it is flipped once
    // here rather than everywhere a point is used.
    y: box.maxY - p.y * SCALE + PAD,
  });

  const activeWalls = active.segments.filter((s) => s.kind === "wall");
  const highlighted = activeWalls.find((w) => w.id === highlightId) ?? null;
  const mid =
    highlighted === null
      ? null
      : shift({
          x: (highlighted.start.x + highlighted.end.x) / 2,
          y: (highlighted.start.y + highlighted.end.y) / 2,
        });

  const points = (outline: readonly { x: number; y: number }[]): string =>
    outline
      .map((p) => {
        const q = shift(p);
        return `${q.x},${q.y}`;
      })
      .join(" ");

  return (
    <svg
      className="mini-plan"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={
        highlightLabel === undefined
          ? `A plan of this floor with ${active.label} picked out`
          : `A plan of this floor with ${highlightLabel} of ${active.label} picked out`
      }
      data-testid="room-mini-plan"
    >
      {/* Every other room, faint: context, not content. */}
      {rooms
        .filter((r) => r.externalRef !== activeRef)
        .map((room) =>
          room.outlines.map((outline, i) => (
            <polygon
              key={`${room.index}-${i}`}
              points={points(outline)}
              className="mini-plan__other"
            />
          )),
        )}

      {/* The room being answered: filled, so the eye lands on it first. */}
      {active.outlines.map((outline, i) => (
        <polygon key={i} points={points(outline)} className="mini-plan__room" />
      ))}

      {activeWalls.map((wall) => {
        const a = shift(wall.start);
        const b = shift(wall.end);
        const on = wall.id === highlightId;
        return (
          <line
            key={wall.id}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            className={on ? "mini-plan__wall mini-plan__wall--on" : "mini-plan__wall"}
          />
        );
      })}

      {/* The number on the wall itself, so the list and the picture use the
          same name for the same thing — which is the point of drawing it. */}
      {mid !== null && highlightLabel !== undefined ? (
        <>
          <circle cx={mid.x} cy={mid.y} r={11} className="mini-plan__pin" />
          <text x={mid.x} y={mid.y} className="mini-plan__pin-text">
            {numberOf(highlightLabel)}
          </text>
        </>
      ) : null}
    </svg>
  );
}

/** "Wall 3" → "3", so the pin stays legible at this size. Anything else stays. */
function numberOf(label: string): string {
  const found = /(\d+)\s*$/.exec(label);
  return found?.[1] ?? label.slice(0, 2);
}

/** The whole floor's extent, so every room is drawn at one scale. */
function bounds(
  rooms: readonly PlanRoom[],
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const points = rooms.flatMap((room) => [
    ...room.outlines.flat(),
    ...room.segments.flatMap((s: PlanSegment) => [s.start, s.end]),
  ]);
  if (points.length === 0) return null;
  const xs = points.map((p) => p.x * SCALE);
  const ys = points.map((p) => p.y * SCALE);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}
